import type {
  CorrelationErrors,
  Correlations,
  UpdateCorrelationsRequestData
} from '@library/types/correlations';
import {
  useDeleteCorrelationsMutation,
  useUpdateCorrelationsMutation
} from '@bo-services/correlationsApi';
import { selectUser } from '@bo-services/authenticationSlice';
import { useCorrelationsPublishStatus } from '@bo-hooks/correlations/useCorrelationsPublishStatus';
import { useSelector } from 'react-redux';

export const useUpdateCorrelations = ({
  handleCorrelationsRefetch,
  handleResetCorrelations
}: {
  handleCorrelationsRefetch: () => Promise<void>;
  handleResetCorrelations: () => void;
}) => {
  const {
    publishStatus: updateCorrelationsStatus,
    setPublishStatus: setUpdateCorrelationsStatus,
    resetPublishStatus: handleResetUpdateCorrelationsStatus
  } = useCorrelationsPublishStatus();

  const [updateCorrelations] = useUpdateCorrelationsMutation();
  const [deleteCorrelations] = useDeleteCorrelationsMutation();
  const user = useSelector(selectUser);

  const handleUpdateCorrelations = async ({
    correlations
  }: {
    correlations: Correlations;
  }) => {
    const errors: CorrelationErrors = [];
    try {
      handleResetUpdateCorrelationsStatus();

      await Promise.all(
        correlations.map(
          ({
            approvedBy,
            correlationId,
            firstPosition,
            firstStatType,
            firstChoice,
            id,
            isSameTeam,
            rowStatus,
            secondChoice,
            secondPosition,
            secondStatType,
            probability,
            requestedBy
          }) => {
            const correlationRequestData: UpdateCorrelationsRequestData = {
              approved_by: approvedBy,
              correlation_id: correlationId,
              first_choice: firstChoice,
              first_position: firstPosition,
              first_stat_type: firstStatType,
              id,
              is_same_team: isSameTeam,
              last_modified_user_name: user?.attributes.email || '',
              probability: Number(probability),
              second_choice: secondChoice,
              second_position: secondPosition,
              second_stat_type: secondStatType,
              requested_by: requestedBy
            };

            return rowStatus === 'UPDATED'
              ? updateCorrelations(correlationRequestData)
                  .unwrap()
                  .catch((error) => errors.push({ correlationId, error }))
              : deleteCorrelations({ id })
                  .unwrap()
                  .catch((error) => errors.push({ correlationId, error }));
          }
        )
      );

      const hasErrors = errors.length > 0;

      setUpdateCorrelationsStatus({
        isError: hasErrors,
        isLoading: false,
        isSuccess: !hasErrors,
        message: hasErrors ? 'Some or all requests has Failed' : 'Success',
        errors
      });

      handleResetCorrelations();

      await handleCorrelationsRefetch();
    } catch (updateError) {
      setUpdateCorrelationsStatus({
        isError: true,
        isLoading: false,
        isSuccess: false,
        message: 'UnExpected Error',
        errors
      });
    }
  };

  return {
    handleResetUpdateCorrelationsStatus,
    handleUpdateCorrelations,
    updateCorrelationsStatus
  };
};
